import React, { useState, useRef } from 'react';
import { machineGuideData } from '../2_data/machineGuideData.js';

// 전체 화면 이미지 모달 컴포넌트
const FullImageModal = ({ image, onClose }) => {
  if (!image) return null;
  return (
    <div 
      className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <img src={image} alt="Full view" className="max-w-full max-h-full object-contain" />
      <button className="absolute top-5 right-5 text-white bg-white/20 p-2 rounded-full">
        <span className="material-icons-round">close</span>
      </button>
    </div>
  );
};

export default function MachineGuidePage({ isActive }) {
  const [activeBrand, setActiveBrand] = useState('all');
  const [selectedImage, setSelectedImage] = useState(null); // 확대 이미지 상태
  const itemRefs = useRef({});

  if (!isActive) return null;

  const filteredGuides = activeBrand === 'all'
    ? machineGuideData.guides
    : machineGuideData.guides.filter(guide => guide.brand === activeBrand);

  const scrollToItem = (id) => {
    itemRefs.current[id]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="w-full max-w-md mx-auto bg-slate-50 min-h-screen pb-24 text-slate-800 font-sans antialiased">
      
      {/* 1. 상단 타이틀 헤더 */}
      <div className="bg-white px-5 py-4 border-b border-slate-100 sticky top-0 z-30 flex items-center justify-between mt-10">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 bg-indigo-50 rounded-xl flex items-center justify-center text-indigo-600">
            <span className="material-icons-round text-xl">layers</span>
          </div>
          <div>
            <h1 className="text-base font-black text-slate-900 tracking-tight">머신 가이드</h1>
            <p className="text-[10px] text-slate-400 font-medium">제조사별 기구 특징</p>
          </div>
        </div>
      </div>

      {/* 2. 브랜드 필터 및 퀵 네비게이션 영역 */}
      <div className="bg-white border-b border-slate-100 sticky top-[73px] z-20">
        <div className="flex items-center gap-2 overflow-x-auto px-4 py-3 scrollbar-none whitespace-nowrap">
          {machineGuideData.brands.map((brand) => (
            <button
              key={brand.id}
              onClick={() => setActiveBrand(brand.id)}
              className={`px-3.5 py-2 text-xs font-bold rounded-xl transition-all ${
                activeBrand === brand.id 
                  ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-200' 
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              {brand.name}
            </button>
          ))}
        </div>

        {filteredGuides.length > 0 && (
          <div className="px-4 pb-3 flex gap-2 overflow-x-auto scrollbar-none border-t border-slate-50">
            {filteredGuides.map((guide) => (
              <button
                key={guide.id}
                onClick={() => scrollToItem(guide.id)}
                className="px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-[10px] font-bold text-slate-600 whitespace-nowrap hover:border-indigo-300 hover:text-indigo-600 transition-colors"
              >
                {guide.name}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* 3. 머신 가이드 리스트 */}
      <div className="p-4 flex flex-col gap-6">
        {filteredGuides.length > 0 ? (
          filteredGuides.map((guide) => (
            <div 
              key={guide.id} 
              ref={(el) => (itemRefs.current[guide.id] = el)}
              className="bg-white rounded-3xl overflow-hidden border border-slate-100 shadow-[0_4px_20px_rgba(0,0,0,0.03)]"
            >
              {/* 이미지 가로 스크롤 영역 */}
              {guide.images && guide.images.length > 0 && (
                <div className="w-full h-72 flex overflow-x-auto snap-x snap-mandatory scrollbar-none bg-slate-100">
                  {guide.images.map((img, idx) => (
                    <div 
                      key={idx}
                      className="min-w-full h-full snap-center cursor-pointer"
                      onClick={() => setSelectedImage(img)}
                    >
                      <img src={img} alt={`${guide.name} ${idx + 1}`} className="w-full h-full object-cover" />
                    </div>
                  ))}
                </div>
              )}
              
              <div className="p-5">
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-lg font-black text-slate-900">{guide.name}</h2>
                  <span className="text-[10px] px-2 py-1 bg-indigo-50 text-indigo-700 font-bold rounded-md uppercase tracking-wider">
                    {guide.target}
                  </span>
                </div>

                {/* 운동 설명 및 설명 이미지 (image_des) */}
                <div className="mb-4">
                  <p className="text-[11px] font-bold text-slate-400 mb-2 uppercase">운동</p>
                  
                  {/* 설명 이미지 영역 */}
                  {guide.image_des && (
                    <div 
                      className="mb-3 rounded-xl overflow-hidden border border-slate-200 cursor-pointer hover:opacity-90 transition-opacity"
                      onClick={() => setSelectedImage(guide.image_des)}
                    >
                      <img src={guide.image_des} alt="설명 이미지" className="w-full h-auto object-cover" />
                    </div>
                  )}

                  <div className="flex flex-col gap-1.5 text-[12px] text-slate-600 leading-relaxed">
                    {Array.isArray(guide.feature) ? guide.feature.map((feat, idx) => (
                      <div key={idx} className="flex gap-2">
                        <span className="text-indigo-500">•</span>
                        <span>{feat}</span>
                      </div>
                    )) : <p>{guide.feature}</p>}
                  </div>
                </div>

                {/* TIP 영역 */}
                {Array.isArray(guide.tip) && guide.tip.length > 0 && (
                  <div className="bg-amber-50 rounded-2xl p-4 border border-amber-100">
                    <div className="flex items-center gap-1.5 text-amber-800 font-black text-[10px] mb-2">
                      <span className="material-icons-round text-sm">workspace_premium</span>
                     TIP
                    </div>
                    <div className="flex flex-col gap-1.5 text-[12px] text-amber-900 font-medium leading-tight">
                      {guide.tip.map((t, idx) => (
                        <div key={idx} className="flex gap-2">
                          <span className="text-amber-400">-</span>
                          <span>{t}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))
        ) : (
          <div className="text-center py-20 text-slate-400 text-sm font-medium">
            해당 브랜드의 가이드 데이터가 없습니다.
          </div>
        )}
      </div>

      {/* 전체 화면 모달 */}
      <FullImageModal image={selectedImage} onClose={() => setSelectedImage(null)} />
    </div>
  );
}